"use client";

import { useEffect, useState } from "react";

const NARROW = "(max-width: 1023px)";
const COARSE = "(pointer: coarse)";
/** localStorage flag: `"1"` forces the desktop shell on a device that would
 *  otherwise be detected as touch. */
export const FORCE_DESKTOP_STORAGE_KEY = "crimp:force-desktop";

function readForceDesktop(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(FORCE_DESKTOP_STORAGE_KEY) === "1";
  } catch {
    // Private mode and quota errors throw here; obey the auto-detection.
    return false;
  }
}

export type TouchLayoutReason = {
  narrowViewport: boolean;
  coarsePointer: boolean;
  forcedDesktop: boolean;
};

function evaluateReason(): TouchLayoutReason {
  if (typeof window === "undefined") {
    return { narrowViewport: false, coarsePointer: false, forcedDesktop: false };
  }
  return {
    narrowViewport: window.matchMedia(NARROW).matches,
    coarsePointer: window.matchMedia(COARSE).matches,
    forcedDesktop: readForceDesktop(),
  };
}

function reasonImpliesTouch(r: TouchLayoutReason): boolean {
  // A narrow desktop shell collapses its two fixed sidebars over the canvas.
  // Ignore the legacy override at phone widths now that the touch editor is
  // fully capable; it still lets a wide 2-in-1 choose desktop controls.
  if (r.narrowViewport) return true;
  if (r.forcedDesktop) return false;
  return r.coarsePointer;
}

/** True when the viewport should render the touch-first editor shell.
 *  Media queries update it live; the storage flag is read once at mount. */
export function useIsTouchLayout(): boolean {
  return useTouchLayoutReason().touch;
}

/** Same evaluation as {@link useIsTouchLayout}, but exposes *why*. */
export function useTouchLayoutReason(): TouchLayoutReason & { touch: boolean } {
  const [reason, setReason] = useState<TouchLayoutReason>(() => evaluateReason());

  useEffect(() => {
    const narrow = window.matchMedia(NARROW);
    const coarse = window.matchMedia(COARSE);
    const update = () => setReason(evaluateReason());
    narrow.addEventListener("change", update);
    coarse.addEventListener("change", update);
    return () => {
      narrow.removeEventListener("change", update);
      coarse.removeEventListener("change", update);
    };
  }, []);

  return { ...reason, touch: reasonImpliesTouch(reason) };
}

/** Reads inside a sentence in the mobile-gate copy, so every branch must stay
 *  a clause. */
export function describeReason(reason: {
  narrowViewport: boolean;
  coarsePointer: boolean;
}): string {
  if (reason.narrowViewport && reason.coarsePointer)
    return "your screen is narrower than 1024 px and your primary pointer is a touchscreen";
  if (reason.narrowViewport) return "your screen is narrower than 1024 px";
  if (reason.coarsePointer) return "your primary pointer is a touchscreen";
  return "of an earlier detection on this device";
}

export type MobileGateDiagnostics = {
  triggers: string;
  screen: string;
  ua: string;
};

export function collectMobileGateDiagnostics(reason: {
  narrowViewport: boolean;
  coarsePointer: boolean;
}): MobileGateDiagnostics {
  const screen =
    typeof window === "undefined"
      ? "unknown"
      : `${window.screen?.width ?? "?"}×${window.screen?.height ?? "?"} screen, ${window.innerWidth}×${window.innerHeight} viewport`;
  const ua = typeof navigator === "undefined" ? "unknown" : navigator.userAgent;
  const triggers =
    [
      reason.narrowViewport ? "narrow viewport" : null,
      reason.coarsePointer ? "coarse pointer" : null,
    ]
      .filter(Boolean)
      .join(" + ") || "unknown";
  return { triggers, screen, ua };
}

/** Persists the flag, then reloads so auth/store boot re-runs for the new shell. */
export function setForceDesktop(forced: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (forced) window.localStorage.setItem(FORCE_DESKTOP_STORAGE_KEY, "1");
    else window.localStorage.removeItem(FORCE_DESKTOP_STORAGE_KEY);
  } catch {
    // Storage unavailable: the reload still applies, but the choice is lost.
  }
  window.location.reload();
}
